import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Box, Typography } from '@mui/material';

import CustomButton from '../../../../../common/CustomButton';
import CustomPassInput from '../../../../../common/CustomPassInput';
import { useChangePasswordMutation } from '../../../../../store/services/authApi';

import styles from './PassSettings.module.scss';

type FormikValues = {
  currentPass?: string;
  newPass?: string;
  confirmNewPass?: string;
};

const PassSettings: FC = () => {
  const [changePassword, { isSuccess }] = useChangePasswordMutation();

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  const initialValues = {
    currentPass: '',
    newPass: '',
    confirmNewPass: '',
  };

  const handleSubmit = async (values: FormikValues) => {
    try {
      await changePassword({
        current_password: values.currentPass,
        new_password: values.newPass,
        confirm_password: values.confirmNewPass,
      }).unwrap();

      setResErrors(null);
    } catch (error) {
      console.error('Error registering user:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[^\s]{8,}$/;

    if (!values.currentPass) {
      errors.currentPass = "Password can't be empty";
    }
    if (!values.newPass) {
      errors.newPass = "Password can't be empty";
    } else if (values.newPass.length <= 8) {
      errors.newPass = 'Password length should be at least 8 characters';
    } else if (!passwordRegex.test(values.newPass)) {
      errors.newPass =
        'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
    if (!values.confirmNewPass) {
      errors.confirmNewPass = "Password can't be empty";
    }
    if (values.newPass !== values.confirmNewPass) {
      errors.confirmNewPass = 'Passwords do not match';
    }

    return errors;
  };

  return (
    <Box className={styles.passSettings}>
      <Typography variant="h7Bold" sx={{color: '#222B3A'}}>Change password</Typography>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        validateOnMount
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: submitForm,
          isSubmitting,
          isValid,
        }) => (
          <form
            className={styles.passSettingsForm}
            onSubmit={submitForm}
            noValidate
          >
            <CustomPassInput
              label="Current Password"
              name="currentPass"
              value={values.currentPass}
              error={touched.currentPass ? errors.currentPass : undefined}
              customClass={styles.customInput}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <CustomPassInput
              label="Create New Password"
              name="newPass"
              value={values.newPass}
              error={touched.newPass ? errors.newPass : undefined}
              customClass={styles.customInput}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <CustomPassInput
              label="Confirm New Password"
              name="confirmNewPass"
              value={values.confirmNewPass}
              error={touched.confirmNewPass ? errors.confirmNewPass : undefined}
              customClass={styles.customInput}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <CustomButton
              btnType="submit"
              customClass={styles.customButton}
              disabled={!isValid || isSubmitting}
            >
              Update Password
            </CustomButton>
            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Password updated successfully.
              </Typography>
            )}
            {resErrors &&
              resErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E' }}
                >
                  {error.detail}
                </Typography>
              ))}
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PassSettings;
